import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  exhaustMap,
  groupBy,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DsToastService } from '@bmw-ds/components';
import { TranslateService } from '@ngx-translate/core';
import { WorkingQueuesApiService } from './working-queues-api.service';
import { IWorkingQueue, WorkingQueueRow } from './working-queues.models';
import { QueueType } from './working-queues.state';
import {
  deleteQueueEntries,
  deleteQueueEntriesFailure,
  deleteQueueEntriesSuccess,
  loadQueueRows,
  loadQueueRowsFailure,
  loadQueueRowsSuccess,
  loadQueueSummaries,
  loadQueueSummariesFailure,
  loadQueueSummariesSuccess,
} from './working-queues.actions';

const DELETE_SUCCESS_KEY = 'workingQueues.deleteSuccess';

@Injectable()
export class WorkingQueuesEffects {
  private readonly actions$ = inject(Actions);
  private readonly workingQueuesApi = inject(WorkingQueuesApiService);
  private readonly toastService = inject(DsToastService);
  private readonly translateService = inject(TranslateService);

  public loadSummaries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadQueueSummaries),
      groupBy(({ queueType }) => queueType),
      mergeMap((group$) =>
        group$.pipe(
          exhaustMap(({ queueType }) =>
            this.summariesSource(queueType).pipe(
              map((summaries) =>
                loadQueueSummariesSuccess({ queueType, summaries })
              ),
              catchError((error) =>
                of(loadQueueSummariesFailure({ queueType, error }))
              )
            )
          )
        )
      )
    )
  );

  public loadRows$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadQueueRows),
      groupBy(({ queueType }) => queueType),
      mergeMap((group$) =>
        group$.pipe(
          switchMap(({ queueType, queue }) =>
            this.rowsSource(queueType, queue).pipe(
              map((rows) =>
                loadQueueRowsSuccess({
                  queueType,
                  queue,
                  rows: Array.isArray(rows) ? rows : [],
                })
              ),
              catchError((error) =>
                of(loadQueueRowsFailure({ queueType, error }))
              )
            )
          )
        )
      )
    )
  );

  public deleteEntries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteQueueEntries),
      switchMap(({ queueType, queue, ids }) =>
        this.deleteSource(queueType, queue, ids).pipe(
          map(() => deleteQueueEntriesSuccess({ queueType, queue })),
          catchError((error) =>
            of(deleteQueueEntriesFailure({ queueType, error }))
          )
        )
      )
    )
  );

  public reloadAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteQueueEntriesSuccess),
      mergeMap(({ queueType, queue }) => [
        loadQueueSummaries({ queueType }),
        loadQueueRows({ queueType, queue }),
      ])
    )
  );

  public notifyDeleteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteQueueEntriesSuccess),
        tap(() =>
          this.toastService.pushToast({
            id: 'wq-delete-success',
            tone: 'positive',
            toastText: this.translateService.instant(DELETE_SUCCESS_KEY),
          })
        )
      ),
    { dispatch: false }
  );

  private deleteSource(
    queueType: QueueType,
    queue: string,
    ids: number[]
  ): Observable<void> {
    if (queueType === 'recovery') {
      return this.workingQueuesApi.deleteRecoveryWorkingQueues(queue, ids);
    }
    if (queueType === 'liability') {
      return this.workingQueuesApi.deleteLiabilityWorkingQueues(queue, ids);
    }
    return this.workingQueuesApi.deleteWorkingQueues(queue, ids);
  }

  private summariesSource(queueType: QueueType): Observable<IWorkingQueue[]> {
    const sources: Record<QueueType, () => Observable<IWorkingQueue[]>> = {
      liability: () => this.workingQueuesApi.getLiabilityDashboard(),
      claim: () => this.workingQueuesApi.getClaimDashboard(),
      recovery: () => this.workingQueuesApi.getRecoveryDashboard(),
    };
    return sources[queueType]();
  }

  private rowsSource(
    queueType: QueueType,
    queue: string
  ): Observable<WorkingQueueRow[]> {
    const sources: Record<QueueType, () => Observable<WorkingQueueRow[]>> = {
      liability: () =>
        this.workingQueuesApi.getLiabilitiesFromWorkingQueue(queue),
      claim: () => this.workingQueuesApi.getClaimFromWorkingQueue(queue),
      recovery: () =>
        this.workingQueuesApi.getRecoveriesFromWorkingQueue(queue),
    };
    return sources[queueType]();
  }
}
